import { useAppSelector } from "@redux/app/hooks"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { useQueryClient } from "@tanstack/react-query"
import { IconButton, Tooltip } from "@ui-lib/index"
import { requestApi2 } from "@utils/http"
import { seempSocket } from "@utils/websocket/seempSocket"
import { Comment, endpoints } from "api"
import { Dispatch, KeyboardEvent, SetStateAction, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { MdCheck, MdClose, MdDelete, MdEdit, MdOutlineChat, MdSend } from "react-icons/md"
import { getHandlePlantCacheUpdated } from "./SeempContent"

type ProcessedComment = {
	authorEmail: string
	authorName: string
	createdAt: Date
	comments: { text: string; id: number; updatedAt?: Date; createdAt: Date }[]
}
const prepareComments = (comments: Comment[], usersList: { email: string; name: string }[]) => {
	const sortedComments = comments.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
	const organizedComments: ProcessedComment[] = []
	for (const [idx, comment] of sortedComments.entries()) {
		if (idx === 0) {
			organizedComments.push({
				authorEmail: comment.author,
				authorName:
					usersList.find((u) => u.email === comment.author)?.name ?? "UNKNOWN USER",
				createdAt: comment.createdAt,
				comments: [
					{
						text: comment.text,
						id: comment.id,
						createdAt: comment.createdAt,
						updatedAt:
							comment.updatedAt.getTime() === comment.createdAt.getTime()
								? undefined
								: comment.updatedAt,
					},
				],
			})
			continue
		}
		const lastComment = organizedComments[organizedComments.length - 1]
		if (lastComment === undefined) {
			continue
		}
		if (
			lastComment.authorEmail === comment.author &&
			comment.createdAt.getTime() - lastComment.createdAt.getTime() <= 1000 * 60 * 1
		) {
			lastComment.comments.push({
				text: comment.text,
				id: comment.id,
				updatedAt:
					comment.updatedAt.getTime() === comment.createdAt.getTime()
						? undefined
						: comment.updatedAt,
				createdAt: comment.createdAt,
			})
		} else {
			organizedComments.push({
				authorEmail: comment.author,
				authorName:
					usersList.find((u) => u.email === comment.author)?.name ?? "UNKNOWN USER",
				createdAt: comment.createdAt,
				comments: [
					{
						text: comment.text,
						id: comment.id,
						updatedAt:
							comment.updatedAt.getTime() === comment.createdAt.getTime()
								? undefined
								: comment.updatedAt,
						createdAt: comment.createdAt,
					},
				],
			})
		}
	}
	return organizedComments
}
const AVATAR_COLORS = [
	"bg-blue-600",
	"bg-yellow-600",
	"bg-pink-600",
	"bg-purple-600",
	"bg-indigo-600",
	"bg-gray-600",
	"bg-green-600",
	"bg-red-600",
] as const

const CommentEntry = ({
	processedComment,
	companyName,
	plantId,
	reportId,
	actionId,
	userEmail,
	avatarColor,
}: {
	processedComment: ProcessedComment
	companyName: string
	plantId: number
	reportId: number
	actionId: number
	userEmail: string
	avatarColor: (typeof AVATAR_COLORS)[number]
}) => {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)
	const [commentsDivRef, setCommentsDivRef] = useState<HTMLDivElement | null>(null)

	const [isCommentBeingUpdated, setIsCommentBeingUpdated] = useState<
		{
			commentId: number
			isUpdating: boolean
		}[]
	>(
		processedComment.comments.map((c) => ({
			commentId: c.id,
			isUpdating: false,
		}))
	)

	useEffect(() => {
		if (processedComment.comments.length === 0) {
			return
		}
		setIsCommentBeingUpdated((prev) => [
			...processedComment.comments.map((c) => {
				const prevComment = prev.find((prevC) => prevC.commentId === c.id)
				if (prevComment === undefined) {
					return {
						commentId: c.id,
						isUpdating: false,
					}
				}
				return prevComment
			}),
		])
	}, [processedComment.comments])

	const handleDeleteComment = async (commentId: number) => {
		const response = await requestApi2(endpoints.seempDeleteComment, {
			id: commentId,
		})
		if (response?.success === true) {
			alert(t("COMMENT DELETED SUCCESSFULLY"))
			seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
			seempSocket.sendPlantCacheUpdateRequest(
				companyName,
				plantId,
				"delete",
				undefined,
				reportId,
				actionId,
				commentId
			)
			handlePlantCacheUpdated({
				plantId,
				updateType: "delete",
				data: undefined,
				reportId: reportId,
				actionId: actionId,
				commentId: commentId,
			})
		} else {
			alert("Failed to delete comment. Please try again later.")
		}
	}

	return (
		<div
			className={`flex flex-row items-end pb-4`}
			ref={(newRef) => setCommentsDivRef(newRef)}
		>
			{userEmail === processedComment.authorEmail ? null : (
				<>
					<div
						className={`${avatarColor} relative inline-block h-6.5 w-6.5 rounded-full border-0 text-center align-middle`}
					>
						<p className="text-tiny absolute top-1/2 left-1/2 -translate-1/2 leading-3 font-bold text-white/90 uppercase">
							{processedComment.authorName.charAt(0).toUpperCase()}
						</p>
					</div>
					<div
						className={`bg-dipai-secondary-100 -mr-px`}
						style={{
							width: "8px",
							height: "10px",
							clipPath: "polygon(100% 0%, 0% 100%, 100% 100%)",
						}}
					/>
				</>
			)}
			<div
				className={`${userEmail === processedComment.authorEmail ? "w-full" : "w-[calc(100%-26px-8px)] pr-7"} flex flex-col`}
			>
				<div
					className={`text-tiny flex flex-row gap-3 ${userEmail === processedComment.authorEmail ? "justify-end" : "justify-start"}`}
				>
					{userEmail === processedComment.authorEmail ? null : (
						<p className={`font-normal`}>{processedComment.authorName}</p>
					)}
					<p className={`text-dipai-secondary-901/80`}>
						{new Date(processedComment.createdAt).toLocaleString()}
					</p>
					{processedComment.comments[0]?.updatedAt !== undefined ? (
						<Tooltip
							collisionBoundary={commentsDivRef}
							arrowClassName="fill-dipai-secondary-700 stroke-white"
							className={
								"bg-dipai-secondary-100 shadow-card border-dipai-secondary-700 text-dipai-secondary-901 border-1"
							}
							customContent={new Date(
								processedComment.comments[0].updatedAt
							).toLocaleString()}
						>
							<p className={`text-tiny text-dipai-secondary-901/80 w-fit`}>
								{t("EDITED").toLowerCase()}
							</p>
						</Tooltip>
					) : null}
				</div>
				<div className={`flex flex-col gap-0.5`}>
					{processedComment.comments.map((c, idx) => (
						<div
							className={`flex flex-col ${userEmail === processedComment.authorEmail ? "items-end" : "items-start"}`}
							key={c.id}
						>
							{idx > 0 && c.updatedAt !== undefined ? (
								<Tooltip
									collisionBoundary={commentsDivRef}
									arrowClassName="fill-dipai-secondary-700 stroke-white"
									className={
										"bg-dipai-secondary-100 shadow-card border-dipai-secondary-700 text-dipai-secondary-901 text-tiny border-1"
									}
									customContent={new Date(c.updatedAt).toLocaleString()}
								>
									<p className={`text-tiny text-dipai-secondary-901/80 w-fit`}>
										{t("EDITED").toLowerCase()}
									</p>
								</Tooltip>
							) : null}
							<div
								className={`group relative flex w-full flex-row ${userEmail === processedComment.authorEmail ? "justify-end" : "justify-start"} gap-2`}
							>
								{isCommentBeingUpdated.find(
									(updateStatus) => updateStatus.commentId === c.id
								)?.isUpdating === false ? (
									<AutoGrowParagraph
										text={c.text}
										id={c.id}
										nComments={processedComment.comments.length}
										userEmail={userEmail}
										commentAuthor={processedComment.authorEmail}
										paragraphIdx={idx}
										handleDeleteComment={handleDeleteComment}
										isCommentBeingUpdated={isCommentBeingUpdated}
										setIsCommentBeingUpdated={setIsCommentBeingUpdated}
									/>
								) : (
									<CommentUpdate
										companyName={companyName}
										plantId={plantId}
										reportId={reportId}
										actionId={actionId}
										userEmail={userEmail}
										commentId={c.id}
										commentText={c.text}
										createdAt={c.createdAt}
										setIsCommentBeingUpdated={setIsCommentBeingUpdated}
									/>
								)}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

function AutoGrowParagraph({
	text,
	id,
	nComments,
	userEmail,
	commentAuthor,
	paragraphIdx,
	isCommentBeingUpdated,
	setIsCommentBeingUpdated,
	handleDeleteComment,
}: {
	text: string
	id: number
	nComments: number
	userEmail: string
	commentAuthor: string
	paragraphIdx: number
	isCommentBeingUpdated: {
		commentId: number
		isUpdating: boolean
	}[]
	setIsCommentBeingUpdated: Dispatch<
		SetStateAction<
			{
				commentId: number
				isUpdating: boolean
			}[]
		>
	>
	handleDeleteComment: (commentId: number) => void
}) {
	const paragraphRef = useRef<HTMLTextAreaElement>(null)
	const mirrorRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const updateDimensions = () => {
			const textarea = paragraphRef.current
			const mirror = mirrorRef.current

			if (textarea === null || mirror === null) return

			textarea.style.width = "100%"
			textarea.style.width = `${mirror.scrollWidth + 2}px`

			textarea.style.height = "32px"
			textarea.style.height = `${textarea.scrollHeight}px`
		}

		updateDimensions()

		window.addEventListener("resize", updateDimensions)
		return () => window.removeEventListener("resize", updateDimensions)
	}, [text])

	return (
		<div
			className={`relative flex w-full ${userEmail === commentAuthor ? "justify-end gap-3" : "justify-start"}`}
		>
			{userEmail === commentAuthor &&
			isCommentBeingUpdated.find((updateStatus) => updateStatus.commentId === id)
				?.isUpdating === false ? (
				<div
					className={`my-auto flex scale-85 flex-row items-center gap-1 opacity-0 transition-all duration-300 ease-in-out group-hover:scale-100 group-hover:opacity-100`}
				>
					<IconButton
						size={"sm"}
						variant={"text-dark"}
						onClick={() =>
							setIsCommentBeingUpdated((prev) =>
								prev
									.filter((status) => status.commentId !== id)
									.concat({
										commentId: id,
										isUpdating: true,
									})
							)
						}
					>
						<IconButton.Icon>{MdEdit}</IconButton.Icon>
					</IconButton>
					<IconButton
						size={"sm"}
						variant={"text-dark"}
						onClick={() => handleDeleteComment(id)}
					>
						<IconButton.Icon>{MdDelete}</IconButton.Icon>
					</IconButton>
				</div>
			) : null}
			<textarea
				value={text}
				disabled={true}
				style={{ height: "32px", width: "100%" }}
				ref={paragraphRef}
				rows={1}
				className={`text-button flex max-w-[calc(100%-3.5rem)] resize-none flex-col rounded-lg border-0 px-2 py-1 ${nComments - 1 === paragraphIdx ? (userEmail === commentAuthor ? "rounded-br-lg" : "rounded-bl-lg") : userEmail === commentAuthor ? "rounded-br-none" : "rounded-bl-none"} ${paragraphIdx !== 0 ? (userEmail === commentAuthor ? "rounded-tr-none" : "rounded-tl-none") : "rounded-t-lg"} ${userEmail === commentAuthor ? "bg-dipai-secondary-800/100 w-fit justify-end rounded-br-none text-white/90" : "bg-dipai-secondary-100 text-dipai-secondary-901/100 rounded-bl-none"} `}
				key={id}
			/>
			<div
				ref={mirrorRef}
				className="text-button bg-alert-erro absolute top-0 -z-1 h-8 w-fit max-w-[calc(100%-3.5rem)] border border-transparent px-2 py-1 break-words whitespace-pre-wrap"
				style={{ visibility: "hidden" }}
			>
				{text}
			</div>
		</div>
	)
}

export function AutoGrowTextarea({
	value,
	setValue,
	onKeyDown,
	isEditInput = false,
	placeHolder = "Type a message...",
	onHeightChange,
	maxLength,
}: {
	value: string
	setValue: (value: string) => void
	onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void
	isEditInput?: boolean
	placeHolder?: string
	onHeightChange?: (height: number) => void
	maxLength?: number
}) {
	const { t } = useTranslation()
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	useEffect(() => {
		const el = textareaRef.current
		if (el !== null) {
			el.style.height = "32px"
			el.style.height = `${el.scrollHeight}px`
			onHeightChange?.(el.offsetHeight)
		}
	}, [onHeightChange, value])

	return (
		<textarea
			ref={textareaRef}
			value={value}
			onChange={(e) =>
				setValue(maxLength == null ? e.target.value : e.target.value.slice(0, maxLength))
			}
			onKeyDown={onKeyDown}
			maxLength={maxLength}
			className={`text-button ${isEditInput === true ? "w-full" : "w-[calc(100%-2.5rem)]"} resize-none overflow-hidden rounded-sm border border-gray-300 px-2 py-1 focus:border-blue-500 focus:ring-0 focus:outline-none`}
			rows={1}
			placeholder={placeHolder}
			style={{ height: "32px" }}
		/>
	)
}

const CommentUpdate = ({
	companyName,
	plantId,
	reportId,
	actionId,
	userEmail,
	commentId,
	commentText,
	createdAt,
	setIsCommentBeingUpdated,
}: {
	companyName: string
	plantId: number
	reportId: number
	actionId: number
	userEmail: string
	commentId: number
	commentText: string
	createdAt: Date
	setIsCommentBeingUpdated: Dispatch<
		SetStateAction<
			{
				commentId: number
				isUpdating: boolean
			}[]
		>
	>
}) => {
	const [value, setValue] = useState(commentText)
	const queryClient = useQueryClient()
	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)

	const handleUpdateComment = async (commentId: number, newText: string, createdAt: Date) => {
		const updatedComment = await requestApi2(endpoints.seempUpdateComment, {
			commentId,
			text: newText,
		})
		if (updatedComment === null) {
			alert("Failed to update comment. Please try again later.")
			return
		}
		seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
		seempSocket.sendPlantCacheUpdateRequest(
			companyName,
			plantId,
			"update",
			{
				id: commentId,
				author: userEmail,
				text: newText,
				createdAt: createdAt,
				updatedAt: updatedComment.updatedAt,
			},
			reportId,
			actionId,
			commentId
		)
		handlePlantCacheUpdated({
			plantId,
			updateType: "update",
			data: {
				id: commentId,
				author: userEmail,
				text: newText,
				createdAt: createdAt,
				updatedAt: updatedComment.updatedAt,
			},
			reportId: reportId,
			actionId: actionId,
			commentId: commentId,
		})
		setIsCommentBeingUpdated((prev) =>
			prev
				.filter((status) => status.commentId !== commentId)
				.concat({
					commentId: commentId,
					isUpdating: false,
				})
		)
	}

	return (
		<div className={`flex w-full flex-col gap-1 pl-16`}>
			<AutoGrowTextarea
				value={value}
				setValue={setValue}
				isEditInput={true}
				onKeyDown={(e) => {
					if (
						e.key === "Enter" &&
						!e.shiftKey
						// value.trim() !== "" &&
						// commentText !== value
					) {
						e.preventDefault()
						if (value.trim() !== "" && commentText !== value) {
							handleUpdateComment(commentId, value, createdAt)
						}
					}
				}}
			/>
			<div className={`flex w-full flex-row justify-end gap-2`}>
				<IconButton
					variant={"text-dark"}
					disabled={value.trim() === ""}
					onClick={() => {
						setIsCommentBeingUpdated((prev) =>
							prev
								.filter((status) => status.commentId !== commentId)
								.concat({
									commentId: commentId,
									isUpdating: false,
								})
						)
					}}
				>
					<IconButton.Icon>{MdClose}</IconButton.Icon>
				</IconButton>
				<IconButton
					variant={"text-dark"}
					disabled={value.trim() === "" || commentText === value}
					onClick={() => {
						handleUpdateComment(commentId, value, createdAt)
					}}
				>
					<IconButton.Icon>{MdCheck}</IconButton.Icon>
				</IconButton>
			</div>
		</div>
	)
}

const CommentInput = ({
	companyName,
	plantId,
	reportId,
	actionId,
	userEmail,
}: {
	companyName: string
	plantId: number
	reportId: number
	actionId: number
	userEmail: string
}) => {
	const [value, setValue] = useState("")
	const queryClient = useQueryClient()
	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)

	const handleCommentSubmit = async (commentText: string) => {
		const newComment = await requestApi2(endpoints.seempAddComment, {
			author: userEmail,
			actionId: actionId,
			text: commentText,
		})
		if (newComment === null) {
			alert("Failed to submit comment. Please try again later.")
			setValue("")
			return
		}

		setValue("")
		seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
		seempSocket.sendPlantCacheUpdateRequest(
			companyName,
			plantId,
			"add",
			newComment,
			reportId,
			actionId,
			newComment.id
		)
		handlePlantCacheUpdated({
			plantId,
			updateType: "add",
			data: newComment,
			reportId: reportId,
			actionId: actionId,
			commentId: newComment.id,
		})
	}

	return (
		<div className={`flex w-full flex-row gap-2 pl-8.25`}>
			<AutoGrowTextarea
				value={value}
				setValue={setValue}
				onKeyDown={(e) => {
					if (e.key === "Enter" && !e.shiftKey) {
						e.preventDefault()
						if (value.trim() !== "") {
							handleCommentSubmit(value)
						}
					}
				}}
			/>
			<IconButton
				variant={"text-dark"}
				disabled={value.trim() === ""}
				onClick={() => {
					handleCommentSubmit(value)
				}}
			>
				<IconButton.Icon>{MdSend}</IconButton.Icon>
			</IconButton>
		</div>
	)
}

export default function SeempDiscussionSection({
	companyName,
	plantId,
	reportId,
	actionId,
	comments,
	usersList,
}: {
	companyName: string
	plantId: number
	reportId: number
	actionId: number
	comments: Comment[]
	usersList: { email: string; name: string }[]
}) {
	const { t } = useTranslation()
	const userData = useAppSelector(getUserData)

	const processedComments = prepareComments(comments, usersList)
	const listOfAvatarColorsPerAuthor: Record<string, (typeof AVATAR_COLORS)[number]> = {}
	for (const comment of processedComments) {
		const currentAuthor = comment.authorEmail
		const amountOfColorsUsed = Object.keys(listOfAvatarColorsPerAuthor).length
		if (listOfAvatarColorsPerAuthor[currentAuthor] !== undefined) {
			continue
		}
		listOfAvatarColorsPerAuthor[currentAuthor] =
			AVATAR_COLORS[Math.min(AVATAR_COLORS.length - 1, amountOfColorsUsed)] ??
			AVATAR_COLORS[0]
	}

	useEffect(() => {
		const container = document.getElementById("messages")
		if (container === null) {
			return
		}
		container.scrollTop = container.scrollHeight
	}, [processedComments])

	return (
		<div
			className={`border-dipai-secondary-901/20 flex flex-col gap-3 rounded-sm border-1 p-4`}
		>
			<div className={`border-dipai-secondary-901/20 flex h-8 flex-row gap-2 border-b-1`}>
				<MdOutlineChat className={"text-dipai-secondary-901 mt-1 h-5 w-5"} />
				<p className={`text-button text-dipai-secondary-901 h-8 font-semibold`}>
					{t("DISCUSSION")}
				</p>
			</div>
			<div
				id={"messages"}
				className={`max-h-100 overflow-y-auto pr-4`}
			>
				{processedComments.length > 0 ? (
					processedComments.map((comment) => (
						<CommentEntry
							key={comment.comments[0]?.id}
							processedComment={comment}
							companyName={companyName}
							plantId={plantId}
							reportId={reportId}
							actionId={actionId}
							userEmail={userData.email}
							avatarColor={
								listOfAvatarColorsPerAuthor[comment.authorEmail] ?? AVATAR_COLORS[0]
							}
						/>
					))
				) : (
					<div className={`p-8 py-6`}>
						<div
							className={`bg-dipai-secondary-100 flex flex-col justify-center gap-4 rounded-lg p-4`}
						>
							<p
								className={`text-menu text-dipai-secondary-901 text-center font-semibold`}
							>
								{t("START A NEW DISCUSSION")}
							</p>
							<p className={`text-tiny text-dipai-secondary-901/80 text-justify`}>
								{t("START A NEW DISCUSSION EXPLANATION")}
							</p>
						</div>
					</div>
				)}
			</div>
			<CommentInput
				companyName={companyName}
				plantId={plantId}
				reportId={reportId}
				actionId={actionId}
				userEmail={userData.email}
			/>
		</div>
	)
}
